import { db, getMeta, setMeta, type Visit } from "./db";
import { api, ApiError, online, currentUser, refreshUser } from "./api";

// Движок синхронизации. Работает сам: при запуске, по таймеру, при появлении сети
// и после каждого сохранения. Сотрудник о нём не думает — видит только счётчик
// «не отправлено».

export type SyncState = {
  running: boolean;
  lastSync: string | null;
  pending: number;
  photosPending: number;
  error: string | null;
};

let state: SyncState = { running: false, lastSync: null, pending: 0, photosPending: 0, error: null };
const listeners = new Set<(s: SyncState) => void>();

export const onSync = (fn: (s: SyncState) => void) => {
  listeners.add(fn);
  fn(state);
  return () => { listeners.delete(fn); };
};

async function emit(patch: Partial<SyncState> = {}) {
  const [pending, photosPending] = await Promise.all([
    db.outbox.count(),
    db.photos.where("uploaded").equals(0).count(),
  ]);
  state = { ...state, ...patch, pending, photosPending };
  listeners.forEach((f) => f(state));
}

export const refreshCounters = () => emit();

/** Скачивание: забираем всё, что изменилось после прошлого раза. */
async function pull() {
  const since = await getMeta<string | null>("lastSync", null);
  const q = since ? "?since=" + encodeURIComponent(since) : "";
  const data = await api<any>("/api/sync" + q);

  await db.transaction(
    "rw",
    // ВСЕ таблицы, к которым обращаемся внутри, должны быть перечислены здесь.
    // Забудешь одну — Dexie откатит транзакцию целиком, и скачивание молча не сработает.
    [db.contractors, db.brands, db.products, db.matrix, db.points, db.contacts,
     db.visits, db.stockMoves, db.outbox, db.meta],
    async () => {
      // Мягко удалённые записи выкидываем локально, остальные кладём поверх.
      const put = async (table: any, rows: any[]) => {
        const dead = rows.filter((r) => r.deletedAt).map((r) => r.id);
        const live = rows.filter((r) => !r.deletedAt);
        if (dead.length) await table.bulkDelete(dead);
        if (live.length) await table.bulkPut(live);
      };
      await put(db.contractors, data.contractors ?? []);
      await put(db.brands, data.brands ?? []);
      await put(db.products, data.products ?? []);
      await put(db.matrix, data.matrix ?? []);
      await put(db.points, data.points ?? []);
      await put(db.contacts, data.contacts ?? []);
      await put(db.stockMoves, data.stockMoves ?? []);

      // Визит, который ещё не уехал, с сервера не перетираем —
      // иначе несохранённые правки пропадут.
      const queued = new Set((await db.outbox.where("kind").equals("visit").toArray()).map((o) => o.entityId));
      const visits = (data.visits ?? []).filter((v: Visit) => !queued.has(v.id));
      await put(db.visits, visits);

      if (data.settings) await db.meta.put({ key: "settings", value: data.settings });
      await db.meta.put({ key: "lastSync", value: data.now });
    }
  );
  // После транзакции, а не внутри: если она откатится, «кто я» не должен
  // обновиться отдельно от остальных данных.
  if (data.me) {
    const cur = currentUser();
    if (!cur || (cur.brandId ?? null) !== (data.me.brandId ?? null) || cur.name !== data.me.name) {
      await refreshUser({ ...cur, ...data.me });
    }
  }
  return data.now as string;
}

/** Отправка: собираем очередь и шлём актуальное состояние записей. */
async function push() {
  const queued = await db.outbox.toArray();
  if (!queued.length) return { rejected: [] as any[] };

  const ids = (kind: string) => queued.filter((q) => q.kind === kind).map((q) => q.entityId);

  const points = (await db.points.bulkGet(ids("point"))).filter(Boolean);
  const contacts = (await db.contacts.bulkGet(ids("contact"))).filter(Boolean);
  const visits = (await db.visits.bulkGet(ids("visit"))).filter(Boolean).map((v: any) => ({
    ...v, userId: undefined, updatedAt: undefined, needsReview: undefined,
  }));

  const res = await api<any>("/api/sync", { method: "POST", body: { points, contacts, visits } });

  // Из очереди убираем только то, что сервер действительно принял.
  const bad = new Set((res.rejected ?? []).map((r: any) => r.id));
  const done = queued.filter((q) => !bad.has(q.entityId)).map((q) => q.key);
  if (done.length) await db.outbox.bulkDelete(done);

  return res;
}

/** Фото уезжают по одному: они тяжёлые, и связь в поле часто рвётся. */
async function pushPhotos() {
  const waiting = await db.photos.where("uploaded").equals(0).toArray();
  for (const p of waiting) {
    // Пока сам визит не уехал, фото прикреплять некуда.
    const stillQueued = await db.outbox.get("visit:" + p.visitId);
    if (stillQueued) continue;

    const form = new FormData();
    form.append("file", p.blob, p.id + ".jpg");
    try {
      // id снимка отдаём свой: повтор после обрыва связи не создаст вторую копию.
      await api(`/api/visits/${p.visitId}/photos?id=${p.id}`, { method: "POST", raw: form, timeoutMs: 60_000 });
      await db.photos.update(p.id, { uploaded: 1 });
      await emit();
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) return; // нет сети — попробуем позже
      // Сервер отверг файл (битый, слишком большой) — не держим очередь вечно.
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        await db.photos.delete(p.id);
        continue;
      }
      return;
    }
  }
}

let inFlight: Promise<void> | null = null;

export function sync(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    if (!online()) { await emit({ error: null }); return; }
    await emit({ running: true, error: null });
    try {
      const res = await push();
      await pushPhotos();
      const now = await pull();
      await setMeta("lastSync", now);
      const rejected = res.rejected ?? [];
      await emit({
        running: false,
        lastSync: now,
        error: rejected.length ? `Сервер не принял записей: ${rejected.length}` : null,
      });
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.status === 0 ? null : e.message)
        : "Не удалось синхронизировать";
      await emit({ running: false, error: msg });
      if (e instanceof ApiError && e.wipeLocal) throw e;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

let timer: number | undefined;

export function startAutoSync() {
  stopAutoSync();
  void sync();
  timer = window.setInterval(() => void sync(), 3 * 60_000);
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisible);
}

export function stopAutoSync() {
  if (timer) window.clearInterval(timer);
  timer = undefined;
  window.removeEventListener("online", onOnline);
  document.removeEventListener("visibilitychange", onVisible);
}

const onOnline = () => void sync();
const onVisible = () => { if (document.visibilityState === "visible") void sync(); };
